<template>
  <div class="widget__content-center widget__content-center--flex">
    <div class="widget__header-text">
      Shows the percentage of visitors who visited the event by number of days.
    </div>
    <div v-if="chartSeries" class="chart-wrapper">
      <apexchart
        height="100%"
        width="100%"
        type="donut"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { WidgetTypeDaysVisited } from '@/types/Widget';
import { pieColors, pieTextColors } from '@/utils/chartColors';
import type { BaseChartOptions } from '@/utils/chartSettings';
import { getChartExportSettings, getDefaultChartOptions } from '@/utils/chartSettings';
import { formatNumber } from '@/utils/formatNumbers';

const props = defineProps<{
  widget: WidgetTypeDaysVisited;
}>();

const chartData = computed(() => {
  if (!props.widget.data || !props.widget.data['Site']) {
    return [];
  }

  const baseData = props.widget.data['Site']['Total'];
  const formatted = Object.keys(baseData).map((key) => {
    const filter = props.widget.filters.time[0].series;
    const title = filter?.find((fil) => `${fil.id}` === `${key}`)?.label;

    const newSeries = {
      name: title,
      data: baseData[key]['Count'],
    };

    return newSeries;
  });

  return formatted;
});

const chartSeries = computed(() => {
  return chartData.value?.map((point) => point.data);
});

const chartLabels = computed(() => {
  if (chartData.value && chartData.value?.length) {
    return chartData.value?.map((point) => point.name || '');
  }
  return [];
});

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = ref<BaseChartOptions>({
  ...defaultChartOptions.value,
  chart: {
    ...defaultChartOptions.value.chart,
    type: 'donut',
    id: 'meshh-chart-distributed-dwell',
    toolbar: getChartExportSettings(props.widget.name, 'Site', 'Total', '', '', ''),
  },
  colors: pieColors,
  labels: chartLabels.value,
  stroke: {
    width: 0,
  },
  plotOptions: {
    pie: {
      customScale: 1,
      donut: {
        size: '50%',
      },
    },
  },
  dataLabels: {
    dropShadow: {
      enabled: false,
    },
    style: {
      colors: pieTextColors,
      fontSize: '12px',
      fontWeight: 'bold',
    },
  },
  tooltip: {
    y: {
      formatter: (val: number) => {
        const sum = chartSeries.value.reduce((partialSum, a) => partialSum + a, 0);
        const perc = formatNumber((val / sum) * 100, 1);
        return `${perc}% (${val}) of visitors.`;
      },
    },
  },
});
</script>
